import { FC, useState, useEffect, useCallback } from 'react';
import { t } from 'i18next';
import Skeleton from 'react-loading-skeleton';
import { useFeature } from '../../store/hooks';
import { tokens, shorthands, Title1 } from '@fluentui/react-components';
import { makeStyles } from '../../Utils/makeStylesWrapper';
import '../../styles/skeletons.css';
import { LayoutGrid, Share, PencilLine, ChevronLeft, ChevronRight, ShieldOff, ArrowUpDown, ArrowUp, ArrowDown, Search } from 'lucide-react';
import { GptSummaryItem } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { useNotification } from '../../Contexts/NotificationContext';
import SyButton from '../Common/SyButton';

const PAGE_SIZE = 10;

const useStyles = makeStyles({
    page: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
    },
    header: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        height: '24px',
        minHeight: '40px',
        maxHeight: '40px',
        flexShrink: 0,
        backgroundColor: tokens.colorNeutralBackground1,
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        ...shorthands.padding('10px', '16px'),
    },
    titleGroup: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
    titleIcon: {
        color: tokens.colorNeutralForeground1,
        flexShrink: 0,
    },
    unauthorized: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground3,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        minHeight: 0,
        background: tokens.colorNeutralBackground3,
        ...shorthands.padding('24px', '32px', '20px', '32px'),
        ...shorthands.gap('16px'),
    },
    tableWrapper: {
        overflowX: 'auto',
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow4,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    table: {
        width: '100%',
        minWidth: '720px',
        tableLayout: 'fixed',
        borderCollapse: 'collapse',
        fontSize: tokens.fontSizeBase300,
    },
    thead: {
        backgroundColor: tokens.colorNeutralBackground2,
        borderBottom: `2px solid ${tokens.colorNeutralStroke2}`,
    },
    th: {
        ...shorthands.padding('12px', '16px'),
        textAlign: 'left',
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground3,
        letterSpacing: '0.06em',
    },
    thSortable: {
        cursor: 'pointer',
        userSelect: 'none',
        ':hover': {
            color: tokens.colorNeutralForeground1,
        },
    },
    thName: {
        width: '30%',
        maxWidth: '30%',
    },
    thAuthor: {
        width: '20%',
        maxWidth: '20%',
    },
    thCenter: {
        textAlign: 'center' as const,
    },
    thUsage: {
        width: '80px',
    },
    thLastEdited: {
        width: '110px',
    },
    thActions: {
        textAlign: 'center' as const,
        width: '170px',
    },
    tdCenter: {
        textAlign: 'center' as const,
    },
    tdNoWrap: {
        whiteSpace: 'nowrap',
    },
    tr: {
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        ':last-child': {
            borderBottom: 'none',
        },
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    td: {
        ...shorthands.padding('12px', '16px'),
        color: 'var(--color-gray-800)',
        verticalAlign: 'middle',
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        fontWeight: tokens.fontWeightMedium,
        minWidth: 0,
    },
    nameCellText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
    },
    nameIcon: {
        color: 'var(--color-gray-600)',
        flexShrink: 0,
    },
    actions: {
        display: 'flex',
        justifyContent: 'center',
        gap: tokens.spacingHorizontalS,
        flexWrap: 'nowrap' as const,
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: tokens.spacingHorizontalS,
        ...shorthands.padding('6px', '0'),
    },
    pageBtn: {
        minWidth: '36px',
        height: '36px',
        ...shorthands.padding('0', '8px'),
        ...shorthands.border('none', 'solid', 'transparent'),
        borderRadius: '50%',
        background: 'transparent',
        color: 'var(--color-gray-600)',
        cursor: 'pointer',
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightRegular,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            color: 'var(--color-gray-800)',
        },
        ':disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
        },
    },
    pageBtnActive: {
        backgroundColor: 'var(--color-gray-200)',
        color: 'var(--color-gray-800)',
        borderTopColor: 'transparent',
        borderRightColor: 'transparent',
        borderBottomColor: 'transparent',
        borderLeftColor: 'transparent',
        fontWeight: tokens.fontWeightSemibold,
        borderRadius: '50%',
        ':hover': {
            backgroundColor: 'var(--color-gray-300)',
        },
    },
    pageBtnNav: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '0 8px',
        height: '36px',
        border: 'none',
        background: 'transparent',
        color: 'var(--color-gray-600)',
        cursor: 'pointer',
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightRegular,
        borderRadius: tokens.borderRadiusMedium,
        ':hover': {
            color: 'var(--color-gray-800)',
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        ':disabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
        },
    },
    ellipsis: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.padding('0', '4px'),
        color: tokens.colorNeutralForeground3,
    },
    sortIcon: {
        marginLeft: '4px',
        opacity: 0.35,
        display: 'inline-flex',
        verticalAlign: 'middle',
        flexShrink: 0,
    },
    sortIconActive: {
        marginLeft: '4px',
        opacity: 1,
        color: tokens.colorBrandForeground1,
        display: 'inline-flex',
        verticalAlign: 'middle',
        flexShrink: 0,
    },
    searchBar: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
        borderRadius: tokens.borderRadiusMedium,
        ...shorthands.padding('0', '16px'),
        height: '48px',
        boxSizing: 'border-box',
        maxWidth: '720px',
        width: '100%',
        ':focus-within': {
            outline: '2px solid var(--color-primary-400)',
            outlineOffset: '2px',
        },
    },
    clearBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '18px',
        height: '18px',
        flexShrink: 0,
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: tokens.colorNeutralForeground3,
        ':hover': {
            color: tokens.colorNeutralForeground1,
        },
    },
    searchIcon: {
        color: tokens.colorNeutralForeground3,
        flexShrink: 0,
    },
    searchInput: {
        flex: 1,
        border: 'none',
        outline: 'none',
        background: 'transparent',
        fontSize: tokens.fontSizeBase400,
        color: tokens.colorNeutralForeground1,
        '::placeholder': {
            color: tokens.colorNeutralForeground4,
        },
    },
    searchError: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorPaletteRedForeground1,
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
    },
    resultCount: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
});

type SortField = 'name' | 'ownerName' | 'usageCount' | 'updatedAt';
type SortDir = 'asc' | 'desc';

function formatDate(d?: Date): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
}

function buildPageNumbers(current: number, total: number): (number | '...')[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (current > 3) pages.push('...');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('...');
    pages.push(total);
    return pages;
}

interface ManageGptsViewProps {
    onGptEdit?: (gpt: GptSummaryItem) => void;
    onGptShare?: (gpt: GptSummaryItem) => void;
}

export const ManageGptsView: FC<ManageGptsViewProps> = ({ onGptEdit, onGptShare }) => {
    const styles = useStyles();
    const chatFeature = useFeature('chat');
    const { dispatchNotification } = useNotification();

    const [items, setItems] = useState<GptSummaryItem[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDir, setSortDir] = useState<SortDir>('asc');
    const [aiFilterInput, setAiFilterInput] = useState('');
    const [searchError, setSearchError] = useState<string | null>(null);
    const [resultCount, setResultCount] = useState<number | null>(null);

    const loadPage = useCallback(
        async (targetPage: number, field: SortField, dir: SortDir, aiFilter?: string) => {
            console.log('[ManageGpts] loadPage called', { targetPage, field, dir, aiFilter });
            setIsLoading(true);
            setSearchError(null);
            await performEnterpriseGPTApiCall(async (client, signal) => {
                try {
                    console.log('[ManageGpts] API request starting', { targetPage, field, dir, aiFilter });
                    const result = await client.getAllGpts(targetPage, PAGE_SIZE, field, dir, aiFilter || undefined, true, signal);
                    console.log('[ManageGpts] API response received', { itemCount: result.items?.length, totalPages: result.totalPages, items: result.items?.map(i => i.name) });
                    if (result.errorMessage) {
                        setSearchError(result.errorMessage);
                    }
                    const deduped = result.items
                        ? result.items.filter((item, idx, arr) => arr.findIndex(x => x.id === item.id) === idx)
                        : [];
                    if (deduped.length !== (result.items?.length ?? 0)) {
                        console.warn('[ManageGpts] Duplicate IDs found in server response!', result.items?.map(i => i.id));
                    }
                    setItems(deduped);
                    setTotalPages(result.totalPages ?? 1);
                    setResultCount(aiFilter ? (result.totalCount ?? null) : null);
                    setPage(targetPage);
                } catch (e: unknown) {
                    if ((e as Error).message !== 'canceled') {
                        dispatchNotification('error.api.get.allGpts', 'error');
                    }
                } finally {
                    setIsLoading(false);
                }
            });
        },
        [dispatchNotification],
    );

    useEffect(() => {
        console.log('[ManageGpts] useEffect fired');
        loadPage(1, sortField, sortDir);
    }, []);   // eslint-disable-line react-hooks/exhaustive-deps


    const handleSort = (field: SortField) => {
        const newDir: SortDir = sortField === field && sortDir === 'asc' ? 'desc' : 'asc';
        console.log('[ManageGpts] handleSort called', { field, newDir });
        setSortField(field);
        setSortDir(newDir);
        loadPage(1, field, newDir, aiFilterInput || undefined);
    };

    const handleClearSearch = () => {
        setAiFilterInput('');
        setResultCount(null);
        loadPage(1, sortField, sortDir, undefined);
    };

    const handlePageChange = (p: number) => {
        if (p < 1 || p > totalPages) return;
        loadPage(p, sortField, sortDir, aiFilterInput || undefined);
    };

    const sortIndicator = (field: SortField) =>
        sortField === field
            ? (sortDir === 'asc'
                ? <ArrowUp size={12} className={styles.sortIconActive} />
                : <ArrowDown size={12} className={styles.sortIconActive} />)
            : <ArrowUpDown size={12} className={styles.sortIcon} />;

    if (!chatFeature.personalGpt?.canManageOtherUsersGPTs) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <LayoutGrid size={20} className={styles.titleIcon} />
                        <Title1 className='settingsTitle'>{t('manageGpts.title', 'Manage GPTs')}</Title1>
                    </div>
                </div>
                <div className={styles.unauthorized}>
                    <ShieldOff size={32} />
                    <span>{t('manageGpts.unauthorized', 'You do not have permission to access this page.')}</span>
                </div>
            </div>
        );
    }

    const renderSkeletonRows = () =>
        Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <tr key={i} className={styles.tr}>
                <td className={styles.td}><Skeleton width='60%' /></td>
                <td className={styles.td}><Skeleton width='50%' /></td>
                <td className={styles.td}><Skeleton width={40} /></td>
                <td className={styles.td}><Skeleton width={80} /></td>
                <td className={styles.td}><Skeleton width={120} /></td>
            </tr>
        ));

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.titleGroup}>
                    <LayoutGrid size={20} className={styles.titleIcon} />
                    <Title1 className='settingsTitle'>{t('manageGpts.title', 'Manage GPTs')}</Title1>
                </div>
            </div>

            <div className={styles.content}>
                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, maxWidth: '720px', width: '100%' }}>
                <div className={styles.searchBar} style={{ flex: 1, maxWidth: '100%' }}>
                    <Search size={16} className={styles.searchIcon} />
                    <input
                        className={styles.searchInput}
                        type='text'
                        value={aiFilterInput}
                        onChange={e => setAiFilterInput(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                loadPage(1, sortField, sortDir, aiFilterInput || undefined);
                            } else if (e.key === 'Escape') {
                                handleClearSearch();
                            }
                        }}
                        placeholder={t('manageGpts.search.placeholder', 'Search GPTs by name, GPT owner, date, status…')}
                        aria-label={t('manageGpts.search.label', 'AI search filter')}
                    />
                    {aiFilterInput && (
                        <button
                            className={styles.clearBtn}
                            onClick={handleClearSearch}
                            aria-label={t('manageGpts.search.clear', 'Clear search')}
                            tabIndex={0}
                        >
                            <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                                <path d='M18 6L6 18' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                                <path d='M6 6L18 18' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                            </svg>
                        </button>
                    )}
                </div>
                {resultCount !== null && !isLoading && (
                    <span className={styles.resultCount}>
                        {t('manageGpts.search.results', 'Results: {{count}}', { count: resultCount })}
                    </span>
                )}
                </div>
                {searchError && (
                    <span className={styles.searchError}>{searchError}</span>
                )}
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead className={styles.thead}>
                            <tr>
                                <th
                                    className={`${styles.th} ${styles.thSortable} ${styles.thName}`}
                                    onClick={() => handleSort('name')}
                                >
                                    {t('manageGpts.col.name', 'GPT NAME')}
                                    {sortIndicator('name')}
                                </th>
                                <th
                                    className={`${styles.th} ${styles.thSortable} ${styles.thAuthor} ${styles.thCenter}`}
                                    onClick={() => handleSort('ownerName')}
                                >
                                    {t('manageGpts.col.author', 'GPT OWNER')}
                                    {sortIndicator('ownerName')}
                                </th>
                                <th
                                    className={`${styles.th} ${styles.thSortable} ${styles.thCenter} ${styles.thUsage}`}
                                    onClick={() => handleSort('usageCount')}
                                >
                                    {t('manageGpts.col.usage', 'GPT USAGE')}
                                    {sortIndicator('usageCount')}
                                </th>
                                <th
                                    className={`${styles.th} ${styles.thSortable} ${styles.thCenter} ${styles.thLastEdited}`}
                                    onClick={() => handleSort('updatedAt')}
                                >
                                    {t('manageGpts.col.lastEdited', 'GPT LAST EDITED')}
                                    {sortIndicator('updatedAt')}
                                </th>
                                <th className={`${styles.th} ${styles.thActions}`}>
                                    {t('manageGpts.col.actions', 'ACTIONS')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading
                                ? renderSkeletonRows()
                                : items.map((gpt) => (
                                    <tr key={gpt.id} className={styles.tr}>
                                        <td className={styles.td}>
                                            <span className={styles.nameCell}>
                                                <LayoutGrid size={16} className={styles.nameIcon} />
                                                <span className={styles.nameCellText} title={gpt.name ?? undefined}>{gpt.name}</span>
                                            </span>
                                        </td>
                                        <td className={`${styles.td} ${styles.tdCenter}`}>{gpt.ownerName ?? '—'}</td>
                                        <td className={`${styles.td} ${styles.tdCenter}`}>{gpt.usageCount ?? 0}</td>
                                        <td className={`${styles.td} ${styles.tdCenter} ${styles.tdNoWrap}`}>{formatDate(gpt.updatedAt)}</td>
                                        <td className={`${styles.td} ${styles.tdCenter}`}>
                                            <div className={styles.actions}>
                                                {onGptShare && (
                                                    <SyButton
                                                        appearance='outline3' fontSize="13px"
                                                        icon={<Share size={14} />} size='extra-small'
                                                        onClick={() => onGptShare(gpt)}
                                                    >
                                                        {t('exploreGpt.share', 'Share')}
                                                    </SyButton>
                                                )}
                                                {onGptEdit && (
                                                    <SyButton
                                                        appearance='outline3' fontSize="13px"
                                                        icon={<PencilLine size={14} />} size='extra-small'
                                                        onClick={() => onGptEdit(gpt)}
                                                    >
                                                        {t('exploreGpt.edit', 'Edit')}
                                                    </SyButton>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>

                {!isLoading && totalPages > 1 && (
                    <nav className={styles.pagination} aria-label='Pagination'>
                        <button
                            className={styles.pageBtnNav}
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 1}
                            aria-label='Previous page'
                        >
                            <ChevronLeft size={14} />
                            {t('manageGpts.pagination.previous', 'Previous')}
                        </button>

                        {buildPageNumbers(page, totalPages).map((p, i) =>
                            p === '...' ? (
                                <span key={`ellipsis-${i}`} className={styles.ellipsis}>…</span>
                            ) : (
                                <button
                                    key={p}
                                    className={`${styles.pageBtn} ${p === page ? styles.pageBtnActive : ''}`}
                                    onClick={() => handlePageChange(p as number)}
                                    aria-label={`Page ${p}`}
                                    aria-current={p === page ? 'page' : undefined}
                                >
                                    {p}
                                </button>
                            ),
                        )}

                        <button
                            className={styles.pageBtnNav}
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page === totalPages}
                            aria-label='Next page'
                        >
                            {t('manageGpts.pagination.next', 'Next')}
                            <ChevronRight size={14} />
                        </button>
                    </nav>
                )}
            </div>
        </div>
    );
};

export default ManageGptsView;
